/* eslint-disable sort-keys-fix/sort-keys-fix -- CSS-in-JS pseudo-selector keys use semantic ordering */
// MUI v4 Tab — NS/Tractie component overrides.
// All MUI v4 style classes are declared explicitly (no implicit inheritance on MUI defaults).
// Tractie deviation: textColorPrimary.selected → NS interactive-fg token (mode-aware).
// transition: none on root (ground-rules.md "Motion and text": no animation).

export const MuiTabLightOverrides = {
  // Tractie TRACTIE Tabs: text hugs the tab, no inline padding, vertical
  // inset-s (12px), leading-l (1.5) so the row stays at 48px. minWidth 0 +
  // maxWidth none so the active indicator wraps the label tightly instead of
  // MUI's 72-264px range. Font-size comes from theme.typography.button (size-m).
  root: {
    minWidth: 0,
    maxWidth: 'none' as const,
    position: 'relative' as const,
    boxSizing: 'border-box' as const,
    minHeight: 48,
    flexShrink: 0,
    padding: '12px 0',
    overflow: 'hidden' as const,
    whiteSpace: 'normal' as const,
    lineHeight: 'var(--ns-font-leading-l)',
    // MUI v4 Tab defaults to fontWeightMedium (500). The inactive Tractie tab
    // is Regular 400 (selected becomes 700 via textColorPrimary.&$selected).
    fontWeight: 400,
    marginInlineEnd: 'var(--ns-space-gap-l)',
    transition: 'none',
    // Focus-visible: Tractie tab outline, 2px in border-focus. Negative offset
    // (inside the edge) so the root's overflow:hidden does not clip the outline.
    // force-focus-visible makes it visible in the comparison grid.
    '&:focus-visible, &.Mui-focusVisible, &.force-focus-visible': {
      outline: '2px solid var(--ns-color-border-focus)',
      outlineOffset: '-2px',
    },
    // MUI v4 Tab sets a minWidth of 160px at breakpoint sm (>=600px) via its own
    // media-query rule that overrides the base minWidth. Tractie hugs the text,
    // so reset that media-query rule to 0 as well.
    '@media (min-width:600px)': {
      minWidth: 0,
    },
  },

  labelIcon: {
    minHeight: 72,
    paddingTop: 9,
  },

  textColorInherit: {
    color: 'inherit' as const,
    opacity: 0.7,
    '&$selected': {
      opacity: 1,
    },
    '&$disabled': {
      opacity: 0.5,
    },
  },

  // Tractie tab text: inactive = content-subtle, selected = content-selected
  // (brand-link blue, mode-aware), weight bold when selected. Hover on the
  // inactive tab darkens the text to content-default (Tractie
  // :host(:hover) { --tractie-tab-color: content-default }). The force-hover
  // variant makes that visible in the comparison grid (no real hover pseudo).
  textColorPrimary: {
    color: 'var(--ns-color-fg-subtle)',
    '&:hover:not($selected):not($disabled), &.force-hover:not($selected):not($disabled)':
      {
        color: 'var(--ns-color-fg-default)',
      },
    '&$selected': {
      color: 'var(--ns-color-fg-interactive-default)',
      fontWeight: 700,
    },
    '&$disabled': {
      color: 'var(--ns-color-fg-disabled)',
    },
  },

  textColorSecondary: {
    color: 'var(--ns-color-fg-subtle)',
    '&:hover:not($selected):not($disabled), &.force-hover:not($selected):not($disabled)':
      {
        color: 'var(--ns-color-fg-default)',
      },
    '&$selected': {
      color: 'var(--ns-color-fg-interactive-default)',
      fontWeight: 700,
    },
    '&$disabled': {
      color: 'var(--ns-color-fg-disabled)',
    },
  },

  selected: {},

  disabled: {},

  fullWidth: {
    flexShrink: 1,
    flexGrow: 1,
    flexBasis: 0,
    maxWidth: 'none' as const,
  },

  wrapped: {
    fontSize: '0.85rem',
    lineHeight: 1.5,
  },

  wrapper: {
    display: 'inline-flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    width: '100%',
    flexDirection: 'column' as const,
  },
} as const;

// Dark variant — token --ns-color-fg-* zijn mode-aware via tokens.css.
export const MuiTabDarkOverrides = {
  ...MuiTabLightOverrides,
} as const;
